import { apiClient } from 'src/lib/apiClient';

const STATS_URL = 'stats';

export interface OverviewStats {
  totalStamps: number;
  totalUsers: number;
  stampsPerDay: {
    current: number;
    previous: number;
    changePercent: number;
  };
  signupsPerDay: {
    current: number;
    previous: number;
    changePercent: number;
  };
  rewards: {
    totalIssued: number;
    totalRedeemed: number;
    totalIssuedByTrigger: number;
    streakIssuedByTrigger: number;
    totalRedeemedByTrigger: number;
    streakRedeemedByTrigger: number;
  };
}

export interface DailyStampCount {
  date: string;
  count: number;
}

export interface UserGrowthPoint {
  date: string;
  totalUsers: number;
}

export interface InactiveMerchant {
  id: number;
  name: string;
  subscriptionStatus: string;
  billingDate: string;
  lastPurchaseDate: string | null;
  totalPurchases: number;
  daysInactive: number;
  inactivityBucket: string;
}

export interface MerchantWithStats {
  id: number;
  name: string;
  location: string | null;
  logoUrl: string;
  type: string;
  subscriptionStatus: string | null;
  subscriptionToken: string | null;
  billingDate: string | null;
  placeId: string | null;
  totalStamps: number;
  lat: number | null;
  lng: number | null;
  dateUpdated: string;
  dateCreated: string;
  referralCode: string | null;
}

export interface StampsByStore {
  merchantId: number;
  merchantName: string;
  count: number;
}

export interface RecentReward {
  id: number;
  userFirstName: string | null;
  userLastName: string | null;
  merchantName: string;
  redeemed: boolean;
  triggerType: string | null;
  dateCreated: string;
}

export interface RecentStamp {
  id: number;
  userFirstName: string | null;
  userLastName: string | null;
  merchantName: string;
  dateCreated: string;
}

export const statsService = {
  getOverviewStats: async (): Promise<OverviewStats> => {
    const response = await apiClient.get(`${STATS_URL}/overview`);
    return response.data;
  },

  getDailyStampPurchases: async (startDate?: string, endDate?: string): Promise<DailyStampCount[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await apiClient.get(`${STATS_URL}/daily-stamps?${params.toString()}`);
    return response.data;
  },

  getUserGrowth: async (): Promise<UserGrowthPoint[]> => {
    const response = await apiClient.get(`${STATS_URL}/user-growth`);
    return response.data;
  },

  getInactiveMerchants: async (): Promise<InactiveMerchant[]> => {
    const response = await apiClient.get(`${STATS_URL}/inactive-merchants`);
    return response.data;
  },

  getMerchantsWithStats: async (): Promise<MerchantWithStats[]> => {
    const response = await apiClient.get(`${STATS_URL}/merchants`);
    return response.data;
  },

  getMerchantsForMap: async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily'): Promise<MerchantWithStats[]> => {
    const response = await apiClient.get(`${STATS_URL}/merchants-for-map?period=${period}`);
    return response.data;
  },

  getStampsByStore: async (startDate?: string, endDate?: string): Promise<StampsByStore[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await apiClient.get(`${STATS_URL}/stamps-by-store?${params.toString()}`);
    return response.data;
  },

  getRecentStamps: async (limit: number = 50, date?: string): Promise<RecentStamp[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (date) params.append('date', date);
    
    const response = await apiClient.get(`${STATS_URL}/recent-stamps?${params.toString()}`);
    return response.data;
  },

  getRecentRewards: async (limit: number = 50): Promise<RecentReward[]> => {
    const response = await apiClient.get(`${STATS_URL}/recent-rewards?limit=${limit}`);
    return response.data;
  }
};
